import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { PageSection } from "@/components/site/PageSection";
import {
  CurrencyNote,
  FaqList,
  H2,
  InsuranceCTA,
  P,
  SITE,
  SpokeCards,
  UL,
  faqSchema,
  medicalWebPageSchema,
  type Faq,
} from "@/components/site/InsuranceProse";
import { TelehealthCta } from "@/components/site/TelehealthCta";

const TITLE =
  "Medicare, TRICARE & FEHB GLP-1 Coverage | Virginia Bariatric";
const DESCRIPTION =
  "The $50 Medicare GLP-1 Bridge, TRICARE's step-therapy and prescriber rules, and what FEHB plans must cover for Wegovy and Zepbound in Virginia.";
const URL = `${SITE}/medical-weight-loss/insurance/medicare-tricare-fehb`;
const NAME = "Medicare, TRICARE, and FEHB Weight-Loss Medication Coverage";

const FAQS: Faq[] = [
  {
    q: "Does Medicare cover Wegovy or Zepbound?",
    a: "Standard Part D still excludes drugs used for weight loss. However, the Medicare GLP-1 Bridge demonstration, which launched July 1, 2026 and runs through December 31, 2027, lets eligible beneficiaries pay a $50 monthly copay for certain GLP-1s prescribed for weight loss. The Bridge is only for people without a Part D-eligible diagnosis; if you have diabetes, sleep apnea, cardiovascular disease, or MASH, the request goes to your regular Part D plan.",
  },
  {
    q: "Does TRICARE require you to try another weight-loss drug first?",
    a: "Yes. TRICARE requires a 3-month trial of generic phentermine, benzphetamine, diethylpropion, or phendimetrazine that failed to produce 5% weight loss, unless you have a contraindication, had an adverse reaction, or lost 5% but still need further reduction. TRICARE also requires at least 6 months of documented behavioral and dietary modification, and the prescriber must be an MTF or TRICARE network provider who has billed TRICARE for assessing you.",
  },
  {
    q: "Do federal employee (FEHB) plans cover weight-loss medication?",
    a: "For plan year 2026, OPM required every FEHB plan to cover at least one GLP-1 prescribed for weight loss plus at least two additional oral anti-obesity medications, so some coverage exists in every FEHB plan — though which drug is preferred varies and prior authorization applies. For plan year 2027, OPM has told carriers it will require intensive behavioral therapy before and during coverage.",
  },
];

export const Route = createFileRoute("/medical-weight-loss/insurance/medicare-tricare-fehb")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/medical-weight-loss/insurance/medicare-tricare-fehb") : []),
    ],
    scripts: import.meta.env.SSR ? [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS, URL)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          medicalWebPageSchema({ url: URL, name: NAME, description: DESCRIPTION })
        ),
      },
    ] : [],
  }),
  component: GovernmentPlansPage,
});

function GovernmentPlansPage() {
  return (
    <>
      <PageSection className="pt-8 pb-4 text-center">
        <h1 className="text-3xl md:text-5xl font-serif">
          Medicare, TRICARE, and Federal Employee Coverage
        </h1>
        <p className="mt-4 max-w-3xl mx-auto text-muted-foreground leading-[1.6]">
          Government plans follow their own rules &mdash; including the new $50 Medicare GLP-1
          Bridge and TRICARE&rsquo;s step-therapy requirement.
        </p>
        <TelehealthCta />

      </PageSection>

      <PageSection maxWidth="4xl" className="pb-4">
        <CurrencyNote />
        <P>
          If you haven&rsquo;t read it yet, the{" "}
          <Link
            to="/medical-weight-loss/insurance"
            preload="intent"
            className="text-brand-deep underline hover:no-underline"
          >
            coverage overview
          </Link>{" "}
          explains the two gates and the diagnosis backdoor that these plans rely on heavily.
        </P>

        <H2 id="medicare">Medicare</H2>
        <P>
          <strong>The default rule:</strong> Medicare Part D has historically excluded drugs used
          for weight loss. That exclusion still governs standard Part D coverage.
        </P>
        <P>
          <strong>What changed on July 1, 2026:</strong> CMS launched the Medicare GLP-1 Bridge, a
          demonstration program running July 1, 2026 through December 31, 2027. Eligible Part D
          beneficiaries pay a <strong>$50 monthly copay</strong> for certain GLP-1 drugs prescribed
          for weight loss &mdash; all formulations of Wegovy (injection and tablets), Wegovy HD,
          Foundayo, and the Zepbound KwikPen specifically.
        </P>
        <P>
          <strong>How eligibility works.</strong> The Bridge is for weight reduction in adults 18
          and older who do not have a Part D-eligible diagnosis. If you have obstructive sleep
          apnea, MASH with moderate-to-advanced fibrosis, type 2 diabetes, or established
          cardiovascular disease, your request goes to your regular Part D plan instead.
        </P>
        <P>Three things to understand about the $50:</P>
        <UL>
          <li>It does <strong>not</strong> count toward your Part D deductible</li>
          <li>It does <strong>not</strong> count toward your annual out-of-pocket cap</li>
          <li>Low-income subsidy assistance does <strong>not</strong> apply to it</li>
        </UL>
        <P>
          <strong>How to start.</strong> A pharmacy claim must first be submitted to &mdash; and
          denied by &mdash; the Bridge processor before your prescriber submits the prior
          authorization. Your prescriber submits the request; patients cannot submit it themselves.
          Beneficiaries can check eligibility at Medicare.gov/glp1bridge or call 1-800-MEDICARE. The
          Bridge runs outside the normal Part D payment flow, so your Part D plan doesn&rsquo;t have
          to opt in for you to use it.
        </P>

        <H2 id="tricare">TRICARE</H2>
        <P>
          TRICARE matters a great deal in Virginia given Hampton Roads and the Northern Virginia
          military community. Its criteria (current prior authorization form, dated July 15, 2026)
          include a step therapy requirement most commercial plans don&rsquo;t have. For adults
          seeking Wegovy or the Zepbound Pen Injector for weight management:
        </P>
        <UL>
          <li>
            BMI of 27 to 29 <strong>with</strong> at least one weight-related condition, or BMI of
            30 or higher
          </li>
          <li>
            Provider attestation of <strong>at least 6 months</strong> of behavioral modification
            and dietary restriction that failed, documented in the medical record
          </li>
          <li>
            A <strong>3-month trial</strong> of generic phentermine, benzphetamine, diethylpropion,
            or phendimetrazine that failed to produce 5% weight loss &mdash; unless you have a
            contraindication, had an adverse reaction, or lost 5% but still need further reduction
          </li>
          <li>
            Not pregnant, not using another GLP-1, and no personal or family history of medullary
            thyroid cancer or MEN type 2
          </li>
        </UL>
        <P>
          <strong>The prescriber must be an MTF or TRICARE network provider</strong> who has billed
          TRICARE for assessing you and developing a treatment plan. This is a hard stop &mdash; the
          request is denied without it.
        </P>
        <P>
          <strong>Ages 12 to 17:</strong> only Wegovy injection is available, requiring BMI at or
          above the 95th percentile. <strong>Approval runs 12 months</strong>, with annual renewal
          requiring at least 5% weight loss from baseline (4% for ages 12 to 17). For Zepbound and
          sleep apnea: adults with a documented apnea-hypopnea index of 15 or more events per hour
          and BMI of 30 or higher; Wegovy is not covered for this indication. Quantity limits are a
          30-day supply at retail, up to 60 days by mail order.
        </P>

        <H2 id="fehb">Federal employees (FEHB)</H2>
        <P>
          Relevant across Northern Virginia. For plan year 2026, OPM required all FEHB plans to
          cover at least one GLP-1 prescribed for weight loss plus at least two additional oral
          anti-obesity medications &mdash; so some coverage exists in every FEHB plan, though which
          drug is preferred varies. Prior authorization applies, and most carriers use the BMI 30,
          or 27-with-comorbidity standard.
        </P>
        <P>
          <strong>Looking ahead:</strong> for plan year 2027, OPM has told carriers it will require
          enrollees to participate in intensive behavioral therapy both before and during coverage
          of anti-obesity medications. Expect FEHB requirements to tighten.
        </P>

        <H2 id="faqs">Frequently asked questions</H2>
        <FaqList faqs={FAQS} />

        <H2 id="more">Other coverage guides</H2>
        <SpokeCards exclude="/medical-weight-loss/insurance/medicare-tricare-fehb" />
      </PageSection>

      <InsuranceCTA />
    </>
  );
}
